/* Core & libraries */
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  POLICY_GRID_ENDPOINT,
  CREATE_POLICY_ENDPOINT,
  EDIT_POLICY_NAME_ENDPOINT,
  DELETE_POLICY_ENDPOINT,
  GET_TEAM_POLICIES_ENDPOINT,
  GET_TEAM_POLICY_ENDPOINT,
  CHANGE_POLICY_METHOD_ENDPOINT,
  VIEW_POLICY_DETAILS_ENDPOINT,
  EDIT_POLICY_DETAILS_ENDPOINT,
  SEARCH_LOCATIONS_ENDPOINT,
  EDIT_SPECIFIC_POLICY_ENDPOINT,
  DELETE_SPECIFIC_POLICY_ENDPOINT,
  CHOICE_ORCH_HOTEL_POLICY_ENDPOINT,
  HOTEL_POLICY_V1_ENDPOINT,
  ASSIGN_HOTEL_POLICY_GROUP_ENDPOINT,
  ASSIGN_POLICY_ENDPOINT,
  GET_ASSIGN_TEAMS_BY_POLICY,
  GET_OPTIONS_ENDPOINT
} from "../endpoints/endpoints";
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";
import { EDIT_POLICY_ROUTE } from "../constants/routes";

/* Components */
/* Styles */

export const usePolicyService = (props = {}) => {
  const { onError } = props;
  const [policyResponse, setPolicyResponse] = useState({})
  const [teamPolicies, setTeamPolicies] = useState();
  const [teamPolicy, setTeamPolicy] = useState();
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const userDetails = useUserContext();
  const navigate = useNavigate();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const isChoice = providerCode?.toUpperCase() === "CHOICE";

  useEffect(() => {
    (async () => {
      await getPolicyList();
    })();
  }, []);

  const getPolicyList = async (options) => {
    if (options) {
      const response = await genericApiFetch({
        url: POLICY_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "policyGridSpinner"
      });
      if (response.success) {
        setPolicyResponse(response.data);
      } else {
        setPolicyResponse({ results: null });
      }
    }
  };

  const deletePolicy = async (policyId) => {
    const response = await genericApiFetch({
      url: DELETE_POLICY_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{policyId}", policyId),
      method: "DELETE",
      spinner: "deleteActionSpinner",
    });
    if (response.success) {
      return true;
    }
    return false;
  }

  const createPolicy = async (policyData) => {
    let payload = {
      policyName: policyData?.policyName
    };
    const response = await genericApiFetch({
      url: CREATE_POLICY_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload,
      spinner: "createPolicySpinner"
    })
    if (response.success) {
      return response.data;
    }
    if (response.success === false && response.errors) {
      return response;
    }
    onError();
    return false;
  };

  const getTeamPolicies = async () => {
    const response = await genericApiFetch({
      url: GET_TEAM_POLICIES_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    });
    if (response.success) {
      setTeamPolicies(response?.data?.policies)
    }
  };
  
  const getTeamPolicy = async (teamId) => {
    const response = await genericApiFetch({
      url: GET_TEAM_POLICY_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{teamId}", teamId),
      method: "GET",
    });
    if (response.success) {
      setTeamPolicy(response?.data?.policy)
    }
  };

  const changePolicy = async (payload, teamId) => {
    const response = await genericApiFetch({
      url: CHANGE_POLICY_METHOD_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{teamId}", teamId),
      method: "PUT",
      data: payload,
      spinner: "changePolicySpinner",
    });
    if (response.success) {
      return true
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorText" />,
      <FormattedMessage id="notifications.revokeErrorTitle" />
    )
    return false
  }

  const getPolicyDetails = async (policyId) => {
    if (isChoice) return getHotelPolicyDetails(policyId);

    const response = await genericApiFetch({
      url: VIEW_POLICY_DETAILS_ENDPOINT.replace('{providerCode}', providerCode?.toLowerCase())
        .replace('{companyEntityId}', companyEntityId).replace('{policyId}', policyId),
      method: 'GET',
    });

    if (response.success) return response.data;

    setNotification(
      "error",
      <FormattedMessage id="notifications.policyErrorTitle" />,
      <FormattedMessage id="notifications.policyErrorText" />
    );
    return false;
  };

  const assignPolicy = async (policyData) => {
    const response = await genericApiFetch({
      url: ASSIGN_POLICY_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{policyId}", policyData.policyId),
      method: "PUT",
      data: policyData.reqBody,
      spinner: "confirmAssignSpinner"
    })
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.assignPolicyTitle" />,
        <FormattedMessage id="notifications.assignPolicyText" values={{ policyName: policyData.policyName }} />
      )
      return response.data;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.assignPolicyErrorTitle" />,
      <FormattedMessage id="notifications.assignPolicyErrorText" />
    );
    return false;
  };

  const getAssignTeamsByPolicyData = async (spinner = "assignTeamsPolicyButton") => {
    const response = await genericApiFetch({
      url: GET_ASSIGN_TEAMS_BY_POLICY.replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: spinner
    })

    if (response.success) {
      return response.data;
    }
    return false
  };

  const editPolicyName = async (policyData, setPolicyResponse) => {
    let payload = {
      policyName: policyData?.policyName
    };
    const response = await genericApiFetch({
      url: EDIT_POLICY_NAME_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId).replace("{policyId}", policyData.policyId),
      method: "PATCH",
      data: payload,
      spinner: "editPolicyNameSpinner"
    })
    if (response.success) {
      if (response?.data?.statusCode === "SUCCESS") {
        const policyResponse = await getPolicyDetails(policyData.policyId)
        setPolicyResponse(policyResponse)
      }
      return response.data;
    }
     if (response.success === false && response.errors) {
      return response;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.editPolicyNameErrorTitle" />,
      <FormattedMessage id="notifications.editPolicyNameErrorText" />
    );
    return false;
  };


  const editPolicyDetails = async (policyData, policyId, firstTimeSaved) => {
    if (isChoice) {
      const success = await updateHotelPolicy(policyId, policyData);
      if (success) {
        setNotification(
          "success",
          <FormattedMessage id="notifications.editedPolicyTitle" />,
          <FormattedMessage id="notifications.editedPolicyText" />
        );
      }
      return success;
    }

    const response = await genericApiFetch({
      url: EDIT_POLICY_DETAILS_ENDPOINT.replace('{providerCode}', providerCode?.toLowerCase())
        .replace('{companyEntityId}', companyEntityId).replace('{policyId}', policyId),
      method: 'PATCH',
      data: policyData,
      spinner: 'editPolicySpinner',
    });

    if (response.success) {
      firstTimeSaved &&
        setNotification(
          "success",
          <FormattedMessage id="notifications.editedPolicyTitle" />,
          <FormattedMessage id="notifications.editedPolicyText" />
        );
      return true;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.policyErrorTitle" />,
      <FormattedMessage id="notifications.policyErrorText" />
    );
    return false;
  };

  const searchLocations = async (searchTerm, spinnerName) => {
    const response = await genericApiFetch({
      url: SEARCH_LOCATIONS_ENDPOINT.replace("{searchTerm}", searchTerm),
      method: "GET",
      spinner: spinnerName
    });
    if (response.success) {
      return response.data
    }
    return false;
  }  

  const editSpecificPolicy = async (data, policyId, isOnCreate) => {
    const response = await genericApiFetch({
      url: EDIT_SPECIFIC_POLICY_ENDPOINT.replace('{providerCode}', providerCode?.toLowerCase())
        .replace('{companyEntityId}', companyEntityId)
        .replace('{policyId}', policyId),
      method: 'PUT',
      data: data,
      spinner: 'editSpecificPolicySpinner',
    });
    if (response.success) {
      if (response?.data?.errors && response?.data?.errors.length > 0) {
        const errorCode = response?.data?.errors[0]?.errorCode;
        if (errorCode === "8002") {
          setNotification(
            "danger",
            <FormattedMessage id="notifications.specificPolicyConflictTitle" />,
            <FormattedMessage id="notifications.specificPolicyConflictText" />,
            false,
            30000,
          );
        } else {
          setNotification(
            "error",
            <FormattedMessage id="notifications.policyErrorTitle" />,
            <FormattedMessage id="notifications.policyErrorText" />
          );
        }
      } else {
        navigate("../" + EDIT_POLICY_ROUTE.replace(":policyId", policyId), {
          state: {
            policyId: policyId,
          },
        });
        isOnCreate
          ? setNotification(
            "success",
            <FormattedMessage id="notifications.createdFlightPolicyTitle" />,
            <FormattedMessage id="notifications.createdFlightPolicyText" />
          )
          : setNotification(
            "success",
            <FormattedMessage id="notifications.editedPolicyTitle" />,
            <FormattedMessage id="notifications.editedPolicyText" />
          );
      }
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.policyErrorTitle" />,
      <FormattedMessage id="notifications.policyErrorText" />
    );
    return false;
  };

  const deleteSpecificPolicy = async (policyId, policyRuleId) => {
    const response = await genericApiFetch({
      url: DELETE_SPECIFIC_POLICY_ENDPOINT.replace('{providerCode}', providerCode?.toLowerCase())
        .replace('{companyEntityId}', companyEntityId)
        .replace('{policyId}', policyId)
        .replace('{policyRuleId}', policyRuleId),
      method: 'DELETE',
    });
    if (response.success) {
      navigate(
        "../" + EDIT_POLICY_ROUTE.replace(":policyId", policyId),
        {
          state: {
            policyId: policyId,
          },
        }
      );
      setNotification(
        "success",
        <FormattedMessage id="notifications.removedFlightPolicyTitle" />,
        <FormattedMessage id="notifications.removedFlightPolicyText" />
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.policyErrorTitle" />,
      <FormattedMessage id="notifications.policyErrorText" />
    );
    return false;
  }

  const getHotelPolicyDetails = async (policyId) => {
    const url = CHOICE_ORCH_HOTEL_POLICY_ENDPOINT
      ? CHOICE_ORCH_HOTEL_POLICY_ENDPOINT
        .replace("{companyEntityId}", companyEntityId)
        .replace("{policyId}", policyId)
      : HOTEL_POLICY_V1_ENDPOINT.replace("{policyId}", policyId);

    const response = await genericApiFetch({
      url,
      method: "GET",
    });

    if (response.success) return response.data;

    setNotification(
      "error",
      <FormattedMessage id="notifications.getHotelPolicyErrorTitle" />,
      <FormattedMessage id="notifications.getHotelPolicyErrorText" />
    );
    return false;
  };

  const updateHotelPolicy = async (policyId, policyData) => {
    const response = await genericApiFetch({
      url: CHOICE_ORCH_HOTEL_POLICY_ENDPOINT
        .replace("{companyEntityId}", companyEntityId)
        .replace("{policyId}", policyId),
      method: "PATCH",
      data: policyData,
      spinner: "editPolicySpinner",
    });

    if (response.success) return true;

    setNotification(
      "error",
      <FormattedMessage id="notifications.policyErrorTitle" />,
      <FormattedMessage id="notifications.policyErrorText" />
    );
    return false;
  };

  const assignHotelPolicyGroup = async ({ policyGroupId, reqBody }) => {
    const response = await genericApiFetch({
      url: ASSIGN_HOTEL_POLICY_GROUP_ENDPOINT.replace(
        "{policyGroupId}",
        policyGroupId
      ),
      method: "POST",
      data: reqBody,
      spinner: "confirmAssignSpinner",
    });

    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.assignPolicyTitle" />,
        <FormattedMessage id="notifications.assignPolicyText" />
      );
      return response.data;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.assignPolicyErrorTitle" />,
      <FormattedMessage id="notifications.assignPolicyErrorText" />
    );
    return false;
  };

   const getBrandOptions = async (options) => {
      const response = await genericApiFetch({
        url: GET_OPTIONS_ENDPOINT,
        queryParams: {optionName: options},
        method: "GET",
      });

      if (response.success) {
        return response.data;
      }
      setNotification(
        "error",
        <FormattedMessage id="notifications.getBrandOptionsErrorTitle" />,
        <FormattedMessage id="notifications.getBrandOptionsErrorText" />
      )
      return false;
    }

    const getCurrencyOptions = async (options) => {
      const response = await genericApiFetch({
        url: GET_OPTIONS_ENDPOINT,
        queryParams: {optionName: options},
        method: "GET",
      });

      if (response.success) {
        return response.data;
      }
      setNotification(
        "error",
        <FormattedMessage id="notifications.getCurrencyOptionsErrorTitle" />,
        <FormattedMessage id="notifications.getCurrencyOptionsErrorText" />
      )
      return false;
    }

  return {
    policyResponse,
    getPolicyList,
    deletePolicy,
    createPolicy,
    getTeamPolicies,
    changePolicy,
    assignPolicy,
    getAssignTeamsByPolicyData,
    editPolicyName,
    teamPolicies,
    getTeamPolicy,
    teamPolicy,
    getPolicyDetails,
    searchLocations,
    editSpecificPolicy,
    deleteSpecificPolicy,
    editPolicyDetails,
    getHotelPolicyDetails,
    updateHotelPolicy,
    assignHotelPolicyGroup,
    getBrandOptions,
    getCurrencyOptions
  };
}
